import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import ColorPicker from '../components/ColorPicker';
import {
  TASK_SUBTHEMES, TASK_CATEGORIES, REFERENCE_LABEL, REFERENCE_PLACEHOLDER,
  REFERENCE_REQUIRED, isGrouped,
} from '../constants';

// Rend <option> ou <optgroup> selon la structure
function renderSubthemeOptions(subthemes) {
  if (!subthemes) return null;
  if (isGrouped(subthemes)) {
    return subthemes.groups.map(g => (
      <optgroup key={g.label} label={g.label}>
        {g.items.map(s => <option key={s} value={s}>{s}</option>)}
      </optgroup>
    ));
  }
  return subthemes.map(s => <option key={s} value={s}>{s}</option>);
}

const PRIORITY_LABEL = { urgent: 'Urgent', important: 'Important', normal: 'Normal' };
const PRIORITY_ORDER = { urgent: 1, important: 2, normal: 3 };

const ORGANIZE_OPTIONS = [
  { value: 'date_asc', label: '📅 Par date (la plus proche d\'abord)' },
  { value: 'urgency', label: '🔥 Par urgence' },
  { value: 'dossier_asc', label: '📁 Par dossier (A → Z)' },
  { value: 'alpha_title', label: '🔤 Par titre (A → Z)' },
  { value: 'created_desc', label: '🕐 Plus récentes d\'abord' },
];

export default function TasksPage() {
  const [tasks, setTasks] = useState([]);
  const [children, setChildren] = useState([]);
  const [dossiers, setDossiers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [filterCat, setFilterCat] = useState('');
  const [filterSub, setFilterSub] = useState('');
  const [filterDossier, setFilterDossier] = useState('');
  const [organizeBy, setOrganizeBy] = useState(() => localStorage.getItem('tasks_organize_by') || 'date_asc');
  const [form, setForm] = useState(emptyForm());
  const [dossierStats, setDossierStats] = useState(null);

  useEffect(() => { localStorage.setItem('tasks_organize_by', organizeBy); }, [organizeBy]);

  // Charger les stats de facturation quand un dossier est filtré
  useEffect(() => {
    if (filterCat === 'travail' && filterDossier) {
      api.get(`/invoices/by-dossier/${encodeURIComponent(filterDossier)}`)
        .then(r => setDossierStats(r.data))
        .catch(() => setDossierStats(null));
    } else {
      setDossierStats(null);
    }
  }, [filterCat, filterDossier]);

  function emptyForm() {
    return { title: '', category: 'perso', subtheme: '', reference: '', child_id: '', priority: 'normal', due_date: '', due_time: '', recurring: 'none', color: '' };
  }

  const load = useCallback(async () => {
    const params = {};
    if (filterCat) params.category = filterCat;
    if (filterSub) params.subtheme = filterSub;
    if (filterDossier) params.reference = filterDossier;
    try {
      const r = await api.get('/tasks', { params });
      setTasks(r.data);
    } catch {}
  }, [filterCat, filterSub, filterDossier]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get('/children').then(r => setChildren(r.data)).catch(() => {});
    api.get('/tasks/meta/dossiers').then(r => setDossiers(r.data)).catch(() => {});
  }, [tasks]);

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...emptyForm(), category: filterCat || 'perso' });
    setShowModal(true);
  };

  const openEdit = (task) => {
    setEditingId(task.id);
    setForm({
      title: task.title,
      category: task.category,
      subtheme: task.subtheme || '',
      reference: task.reference || '',
      child_id: task.child_id || '',
      priority: task.priority,
      due_date: task.due_date ? task.due_date.split('T')[0] : '',
      due_time: task.due_time || '',
      recurring: task.recurring,
      color: task.color || '',
    });
    setShowModal(true);
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      child_id: form.child_id || null,
      subtheme: form.subtheme || null,
      reference: form.reference || null,
      due_date: form.due_date || null,
      due_time: form.due_time || null,
      color: form.color || null,
    };
    try {
      if (editingId) await api.put(`/tasks/${editingId}`, payload);
      else await api.post('/tasks', payload);
      setShowModal(false);
      setForm(emptyForm());
      setEditingId(null);
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur');
    }
  };

  const toggle = async (task) => {
    await api.patch(`/tasks/${task.id}/status`, { status: task.status === 'fait' ? 'a_faire' : 'fait' });
    load();
  };

  const deleteTask = async (id) => {
    if (!window.confirm('Supprimer cette tâche ?')) return;
    await api.delete(`/tasks/${id}`);
    load();
  };

  const currentSubthemes = TASK_SUBTHEMES[form.category];
  const useChildDropdown = form.category === 'enfants';
  const refLabel = REFERENCE_LABEL[form.category] || 'Référence';
  const refPlaceholder = REFERENCE_PLACEHOLDER[form.category] || '';
  const refRequired = REFERENCE_REQUIRED[form.category] || false;

  // Organisation unifiée : groupe + tri interne selon le mode choisi
  const organized = React.useMemo(() => {
    // Tri de base interne (pour les vues groupées)
    const sortByDateTime = (a, c) => {
      const da = a.due_date || '9999-12-31';
      const dc = c.due_date || '9999-12-31';
      if (da !== dc) return da.localeCompare(dc);
      if (a.due_time && c.due_time) return a.due_time.localeCompare(c.due_time);
      if (a.due_time) return -1;
      if (c.due_time) return 1;
      return (PRIORITY_ORDER[a.priority] || 9) - (PRIORITY_ORDER[c.priority] || 9);
    };

    // ─── 1) Groupement par DATE ─────────────────────────
    if (organizeBy === 'date_asc') {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayStr = today.toISOString().split('T')[0];
      const tomorrow = new Date(today);
      tomorrow.setDate(today.getDate() + 1);
      const tomorrowStr = tomorrow.toISOString().split('T')[0];

      const retard = { key: 'retard', label: 'En retard', emoji: '🔴', tasks: [], color: 'var(--danger)' };
      const todayB = { key: 'today', label: "Aujourd'hui", emoji: '☀️', tasks: [], color: 'var(--primary)' };
      const tomorrowB = { key: 'tomorrow', label: 'Demain', emoji: '➡️', tasks: [], color: 'var(--text)' };
      const nodate = { key: 'nodate', label: 'Sans date', emoji: '⏳', tasks: [], color: 'var(--text-muted)' };
      const futureByDate = {};

      tasks.forEach(t => {
        if (!t.due_date) { nodate.tasks.push(t); return; }
        const d = t.due_date.split('T')[0];
        if (d < todayStr && t.status !== 'fait') retard.tasks.push(t);
        else if (d === todayStr) todayB.tasks.push(t);
        else if (d === tomorrowStr) tomorrowB.tasks.push(t);
        else {
          if (!futureByDate[d]) {
            const dt = new Date(d + 'T00:00');
            const label = dt.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
            futureByDate[d] = { key: d, label: label.charAt(0).toUpperCase() + label.slice(1), emoji: '📅', tasks: [], color: 'var(--text)' };
          }
          futureByDate[d].tasks.push(t);
        }
      });

      const sortInternal = (a, c) => {
        if (a.due_time && c.due_time) return a.due_time.localeCompare(c.due_time);
        if (a.due_time) return -1;
        if (c.due_time) return 1;
        return (PRIORITY_ORDER[a.priority] || 9) - (PRIORITY_ORDER[c.priority] || 9);
      };
      [retard, todayB, tomorrowB, nodate, ...Object.values(futureByDate)].forEach(b => b.tasks.sort(sortInternal));

      const buckets = [
        ...(retard.tasks.length ? [retard] : []),
        ...(todayB.tasks.length ? [todayB] : []),
        ...(tomorrowB.tasks.length ? [tomorrowB] : []),
        ...Object.values(futureByDate).sort((a, b) => a.key.localeCompare(b.key)),
        ...(nodate.tasks.length ? [nodate] : []),
      ];
      return { type: 'grouped', groups: buckets, contextLabel: 'bucket' };
    }

    // ─── 2) Groupement par DOSSIER ──────────────────────
    if (organizeBy === 'dossier_asc') {
      const groups = {};
      tasks.forEach(t => {
        const key = (t.category === 'travail' && t.reference && t.reference.trim())
          ? t.reference
          : '(Sans dossier)';
        if (!groups[key]) groups[key] = [];
        groups[key].push(t);
      });
      const result = Object.keys(groups)
        .sort((a, b) => {
          if (a === '(Sans dossier)') return 1;
          if (b === '(Sans dossier)') return -1;
          return a.localeCompare(b, 'fr');
        })
        .map(k => ({ key: k, label: k, emoji: '📁', tasks: groups[k].sort(sortByDateTime) }));
      return { type: 'grouped', groups: result, contextLabel: 'dossier' };
    }

    // ─── 3) Tri par URGENCE (liste plate) ───────────────
    if (organizeBy === 'urgency') {
      const sorted = [...tasks].sort((a, c) => {
        const p = (PRIORITY_ORDER[a.priority] || 9) - (PRIORITY_ORDER[c.priority] || 9);
        if (p !== 0) return p;
        return sortByDateTime(a, c);
      });
      return { type: 'flat', tasks: sorted };
    }

    // ─── 4) Tri ALPHABÉTIQUE sur TITRE ─────────────────
    if (organizeBy === 'alpha_title') {
      const sorted = [...tasks].sort((a, c) => (a.title || '').localeCompare(c.title || '', 'fr'));
      return { type: 'flat', tasks: sorted };
    }

    // ─── 5) Plus RÉCENTES d'abord (création desc) ──────
    if (organizeBy === 'created_desc') {
      const sorted = [...tasks].sort((a, c) => (c.created_at || '').localeCompare(a.created_at || ''));
      return { type: 'flat', tasks: sorted };
    }

    return { type: 'flat', tasks };
  }, [tasks, organizeBy]);

  return (
    <div>
      <div className="page-header page-header-row">
        <div>
          <h1>Tâches & Organisation</h1>
          <p>
            {tasks.length} tâche{tasks.length !== 1 ? 's' : ''}
            {organized.type === 'grouped' ? ` · ${organized.groups.length} ${organized.contextLabel === 'dossier' ? 'dossier' : 'période'}${organized.groups.length !== 1 ? 's' : ''}` : ''}
          </p>
        </div>
        <button className="btn btn-primary" onClick={openCreate}>+ Nouvelle tâche</button>
      </div>

      {/* Sélecteur d'organisation */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        <label style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-light)' }}>Organiser :</label>
        <select
          className="form-input"
          style={{ maxWidth: 300, fontSize: '0.85rem', fontWeight: 600 }}
          value={organizeBy}
          onChange={e => setOrganizeBy(e.target.value)}
        >
          {ORGANIZE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>

      <div className="task-filters">
        <button className={`filter-btn ${filterCat === '' ? 'active' : ''}`} onClick={() => { setFilterCat(''); setFilterSub(''); setFilterDossier(''); }}>Toutes</button>
        {TASK_CATEGORIES.map(c => (
          <button key={c.value} className={`filter-btn ${filterCat === c.value ? 'active' : ''}`} onClick={() => { setFilterCat(filterCat === c.value ? '' : c.value); setFilterSub(''); setFilterDossier(''); }}>
            {c.emoji} {c.label}
          </button>
        ))}
      </div>

      {/* Filtres secondaires si une catégorie est sélectionnée */}
      {filterCat && (
        <div className="task-filters" style={{ marginTop: -8, marginBottom: 16 }}>
          <select className="form-input" style={{ maxWidth: 240, fontSize: '0.82rem' }} value={filterSub} onChange={e => setFilterSub(e.target.value)}>
            <option value="">Tous sous-thèmes</option>
            {renderSubthemeOptions(TASK_SUBTHEMES[filterCat])}
          </select>
          {filterCat === 'travail' && dossiers.length > 0 && (
            <select className="form-input" style={{ maxWidth: 240, fontSize: '0.82rem' }} value={filterDossier} onChange={e => setFilterDossier(e.target.value)}>
              <option value="">Tous dossiers</option>
              {dossiers.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          )}
        </div>
      )}

      {/* Encart facturation si filtre dossier actif */}
      {dossierStats && filterDossier && (
        <div className="card" style={{ marginBottom: 16, padding: 14, background: 'var(--primary-bg)', border: '1px solid var(--primary-light)' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 16 }}>
            <div style={{ fontSize: '1.05rem', fontWeight: 700 }}>📁 {filterDossier}</div>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginLeft: 'auto' }}>
              <div><span style={{ color: 'var(--text-muted)', fontSize: '0.75rem', fontWeight: 600 }}>Facturé</span><div style={{ fontWeight: 700 }}>{Number(dossierStats.total_facture).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</div></div>
              <div><span style={{ color: 'var(--text-muted)', fontSize: '0.75rem', fontWeight: 600 }}>Encaissé</span><div style={{ fontWeight: 700, color: 'var(--success)' }}>{Number(dossierStats.total_paye).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</div></div>
              <div><span style={{ color: 'var(--text-muted)', fontSize: '0.75rem', fontWeight: 600 }}>Reste dû</span><div style={{ fontWeight: 700, color: (dossierStats.total_facture - dossierStats.total_paye) > 0 ? 'var(--warning)' : 'var(--text-muted)' }}>{Number(dossierStats.total_facture - dossierStats.total_paye).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</div></div>
              <div><span style={{ color: 'var(--text-muted)', fontSize: '0.75rem', fontWeight: 600 }}>Factures</span><div style={{ fontWeight: 700 }}>{dossierStats.nb_factures}</div></div>
            </div>
          </div>
        </div>
      )}

      {tasks.length === 0 ? (
        <div className="empty-state">
          <div className="empty-emoji">📋</div>
          <p>Aucune tâche. Ajoutez votre première tâche !</p>
        </div>
      ) : organized.type === 'grouped' ? (
        <div>
          {organized.groups.map(group => (
            <div key={group.key} style={{ marginBottom: 20 }}>
              <div className="day-section-title" style={group.color ? { color: group.color } : {}}>
                <span className="section-emoji">{group.emoji}</span>
                {group.label}
                <span style={{ marginLeft: 'auto', fontSize: '0.72rem', color: 'var(--text-muted)', fontWeight: 500 }}>
                  {group.tasks.filter(t => t.status === 'fait').length}/{group.tasks.length}
                </span>
              </div>
              <div className="task-list">
                {group.tasks.map(task => (
                  <TaskItem
                    key={task.id}
                    task={task}
                    onToggle={toggle}
                    onEdit={openEdit}
                    onDelete={deleteTask}
                    inDossierGroup={organized.contextLabel === 'dossier'}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="task-list">
          {organized.tasks.map(task => <TaskItem key={task.id} task={task} onToggle={toggle} onEdit={openEdit} onDelete={deleteTask} />)}
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" /* Pas de fermeture au clic extérieur */>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
              <h2 style={{ margin: 0 }}>{editingId ? 'Modifier la tâche' : 'Nouvelle tâche'}</h2>
              <div style={{ display: 'flex', gap: 6 }}>
                <button
                  type="button"
                  className="btn-icon"
                  title="Ouvrir dans une nouvelle fenêtre"
                  onClick={() => {
                    const url = editingId ? `/taches/${editingId}?popup=1` : `/taches/new?popup=1`;
                    window.open(url, `task-${editingId || 'new'}`, 'width=780,height=820,menubar=no,toolbar=no,location=no,status=no');
                    setShowModal(false);
                  }}
                >
                  ↗️
                </button>
                <button type="button" className="btn-icon" title="Fermer" onClick={() => setShowModal(false)}>✕</button>
              </div>
            </div>
            <form onSubmit={save}>
              <div className="form-group">
                <label>Titre / Description</label>
                <input className="form-input" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Ex : Rédiger conclusions, Acheter cadeau anniv..." required />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Catégorie</label>
                  <select className="form-input" value={form.category} onChange={e => setForm({ ...form, category: e.target.value, subtheme: '', reference: '', child_id: '' })}>
                    {TASK_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.emoji} {c.label}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Sous-thème</label>
                  <select className="form-input" value={form.subtheme} onChange={e => setForm({ ...form, subtheme: e.target.value })}>
                    <option value="">—</option>
                    {renderSubthemeOptions(currentSubthemes)}
                  </select>
                </div>
              </div>

              {/* Champ référence contextuel */}
              <div className="form-group">
                <label>{refLabel}{refRequired ? ' *' : ''}</label>
                {useChildDropdown ? (
                  <select className="form-input" value={form.child_id} onChange={e => setForm({ ...form, child_id: e.target.value, reference: '' })}>
                    <option value="">— Choisir un enfant —</option>
                    {children.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    <option value="all">Tous / Fratrie</option>
                  </select>
                ) : (
                  <input
                    className="form-input"
                    value={form.reference}
                    onChange={e => setForm({ ...form, reference: e.target.value })}
                    placeholder={refPlaceholder}
                    list={form.category === 'travail' ? 'dossiers-list' : undefined}
                    required={refRequired}
                  />
                )}
                {form.category === 'travail' && (
                  <datalist id="dossiers-list">
                    {dossiers.map(d => <option key={d} value={d} />)}
                  </datalist>
                )}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Priorité</label>
                  <select className="form-input" value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })}>
                    <option value="urgent">Urgent</option>
                    <option value="important">Important</option>
                    <option value="normal">Normal</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Récurrence</label>
                  <select className="form-input" value={form.recurring} onChange={e => setForm({ ...form, recurring: e.target.value })}>
                    <option value="none">Unique</option>
                    <option value="daily">Chaque jour</option>
                    <option value="weekly">Chaque semaine</option>
                    <option value="monthly">Chaque mois</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Date</label>
                  <input className="form-input" type="date" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Heure</label>
                  <input className="form-input" type="time" value={form.due_time} onChange={e => setForm({ ...form, due_time: e.target.value })} />
                </div>
              </div>

              <ColorPicker value={form.color} onChange={c => setForm({ ...form, color: c })} label="Couleur de la tâche" />

              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Annuler</button>
                <button type="submit" className="btn btn-primary">{editingId ? 'Enregistrer' : 'Créer'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function TaskItem({ task, onToggle, onEdit, onDelete, inDossierGroup = false }) {
  const catLabel = TASK_CATEGORIES.find(c => c.value === task.category)?.label || task.category;
  const colorStyle = task.color ? { borderLeft: `5px solid ${task.color}`, paddingLeft: 11 } : {};

  // Pour les tâches travail avec dossier hors vue groupée : afficher le dossier comme titre principal
  const showDossierAsPrimary = !inDossierGroup && task.category === 'travail' && task.reference;
  // Pour enfants : afficher le nom de l'enfant en référence
  const childDisplay = task.child_id ? `👶 ${task.child_name || 'Enfant'}` : null;

  return (
    <div className={`task-item ${task.status === 'fait' ? 'done' : ''}`} style={colorStyle}>
      <div className={`task-checkbox ${task.status === 'fait' ? 'checked' : ''}`} onClick={() => onToggle(task)}>
        {task.status === 'fait' && <span>✓</span>}
      </div>
      <div className="task-info" style={{ cursor: 'pointer' }} onClick={() => onEdit(task)}>
        {showDossierAsPrimary ? (
          <>
            <div className="task-title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: '0.9rem' }}>📁</span>
              <span>{task.reference}</span>
            </div>
            <div style={{ fontSize: '0.82rem', color: 'var(--text-light)', fontWeight: 500, marginTop: 2 }}>
              {task.title}
            </div>
          </>
        ) : (
          <div className="task-title">{task.title}</div>
        )}
        <div className="task-meta">
          <span className={`badge badge-${task.priority}`}>{PRIORITY_LABEL[task.priority]}</span>
          <span className={`badge badge-${task.category}`}>{catLabel}</span>
          {task.subtheme && <span className="badge" style={{ background: '#f1f5f9', color: '#475569' }}>{task.subtheme}</span>}
          {childDisplay && <span style={{ fontSize: '0.75rem', color: 'var(--text-light)', fontWeight: 600 }}>{childDisplay}</span>}
          {task.due_date && (
            <span className="task-date">
              📅 {new Date(task.due_date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
              {task.due_time && ' · ' + task.due_time.slice(0, 5)}
            </span>
          )}
          {task.recurring !== 'none' && <span className="badge" style={{ background: '#f0fdf4', color: 'var(--success)' }}>🔄 {task.recurring}</span>}
        </div>
      </div>
      <button className="btn-ghost" onClick={() => onDelete(task.id)} title="Supprimer">🗑️</button>
    </div>
  );
}
